// ============================================
// Auth - User Module (ES6)
// 사용자 데이터 관리
// ============================================

import { USER_ENDPOINTS } from '../shared/api-endpoints.js';
import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { authFetch } from '../shared/auth-fetch.js';
import {
  getCurrentUser, getAuthDb,
  getUserCarbon, setUserCarbon, getUserNickname, setUserNickname,
  getCachedProfiles, setCachedProfiles,
  getCachedPrimaryProfileId, setCachedPrimaryProfileId as setProfileId,
  setIsAdmin
} from './state.js';
import { updateNavVisibility } from '../app/navigation.js';

// ============================================
// Load User Data
// ============================================

export async function loadUserData(retryCount = 0) {
  const currentUser = getCurrentUser();
  if (!currentUser) return;
  const maxRetries = 3;

  const authDb = getAuthDb();

  try {
    const [userDoc, profilesSnap, creatorsDoc] = await Promise.all([
      authDb.collection('users').doc(currentUser.uid).get(),
      authDb.collection('users').doc(currentUser.uid).collection('profiles').get(),
      authDb.collection('creators').doc(currentUser.uid).get()
    ]);

    if (!userDoc.exists) {
      const response = await authFetch(USER_ENDPOINTS.loadUserData, {
        body: {}
      });
      if (!response.ok) throw new Error('유저 데이터 생성 실패');
      const data = await response.json();
      setUserCarbon(data.carbon || 0);
      setUserNickname(data.nickname || '');
      setCachedProfiles(data.profiles || []);
      window.userAvatarUrl = data.avatarUrl || null;
      window.userAvatarCrop = data.avatarCrop || null;
      window.isOfficialCreator = data.isOfficialCreator || false;
    } else {
      const userData = userDoc.data();
      setUserCarbon(userData.carbon || 0);
      setProfileId(userData.primaryProfileId || null);
      setCachedProfiles(profilesSnap.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        gender: doc.data().gender || '남성',
        description: doc.data().description || '',
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || null
      })));

      if (creatorsDoc.exists) {
        const creatorsData = creatorsDoc.data();
        setUserNickname(creatorsData.nickname || '');
        window.userAvatarUrl = creatorsData.avatarUrl || null;
        window.userAvatarCrop = creatorsData.avatarCrop || null;
        window.isOfficialCreator = creatorsData.isOfficialCreator || false;
      } else {
        setUserNickname(userData.nickname || '');
        window.userAvatarUrl = userData.avatarUrl || null;
        window.userAvatarCrop = userData.avatarCrop || null;
        window.isOfficialCreator = userData.isOfficialCreator || false;
      }
    }

    updateCarbonDisplay();
    updateNicknameDisplay();
    updateAvatarDisplay();
  } catch (e) {
    console.error(`Failed to load user data (attempt ${retryCount + 1}):`, e);
    if (retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return loadUserData(retryCount + 1);
    }
  }
}

// ============================================
// Check Admin Status
// ============================================

export async function checkAdminStatus() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    setIsAdmin(false);
    updateNavVisibility(false);
    return;
  }

  try {
    const response = await authFetch(USER_ENDPOINTS.checkAdmin, { method: 'GET' });
    if (response.ok) {
      const { isAdmin } = await response.json();
      setIsAdmin(isAdmin);
      updateNavVisibility(isAdmin);
      console.log('[User] Admin status:', isAdmin);
    } else {
      setIsAdmin(false);
      updateNavVisibility(false);
    }
  } catch (e) {
    console.error('[User] Admin check failed:', e);
    setIsAdmin(false);
    updateNavVisibility(false);
  }
}

// ============================================
// Display Updates
// ============================================

export function updateNicknameDisplay() {
  const nicknameEl = document.getElementById('myPageNickname');
  if (nicknameEl) {
    nicknameEl.textContent = getUserNickname() || '닉네임 없음';
  }
}

export function updateAvatarDisplay() {
  const avatarEl = document.getElementById('myPageAvatarImage');
  if (avatarEl && window.userAvatarUrl) {
    const position = window.userAvatarCrop?.objectPosition || 'center 20%';
    avatarEl.innerHTML = `<img src="${window.userAvatarUrl}" alt="프로필" style="width: 100%; height: 100%; object-fit: cover; object-position: ${position};">`;
  }
}

export function updateCarbonDisplay() {
  const carbonValue = getUserCarbon().toLocaleString();

  const carbonEl = document.getElementById('carbonAmount');
  if (carbonEl) {
    carbonEl.textContent = carbonValue;
  }

  const shopCarbonEl = document.getElementById('shopCarbonAmount');
  if (shopCarbonEl) {
    shopCarbonEl.textContent = carbonValue;
  }

  const myPageCarbonEl = document.getElementById('myPageCarbon');
  if (myPageCarbonEl) {
    myPageCarbonEl.textContent = carbonValue;
  }

  const chatCarbonEl = document.getElementById('chatCarbonAmount');
  if (chatCarbonEl) {
    chatCarbonEl.textContent = carbonValue;
  }
}

// ============================================
// Nickname Modal
// ============================================

export function openNicknameModal() {
  const existing = document.getElementById('nicknameModal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'nicknameModal';
  modal.className = 'bottom-sheet-overlay';
  modal.innerHTML = `
    <div class="bottom-sheet-backdrop" data-action="close"></div>
    <div class="bottom-sheet" style="max-height: 50vh;">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-header">
        <h3>닉네임 변경</h3>
      </div>
      <div class="bottom-sheet-body">
        <div class="form-group">
          <label style="font-size: 13px; margin-bottom: 8px; display: block; color: var(--text-secondary);">닉네임 (2~12자)</label>
          <input type="text" id="nicknameInput" placeholder="새 닉네임 입력" maxlength="12"
            style="width: 100%; padding: 12px; background: var(--bg-tertiary); border: 1px solid var(--border-light, rgba(128,128,128,0.15)); border-radius: 8px; color: var(--text-primary); font-size: 14px; box-sizing: border-box;">
        </div>
        <button class="btn btn-primary" style="width: 100%; margin-top: 16px;" data-action="save">저장</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // 이벤트 위임
  modal.addEventListener('click', (e) => {
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (action === 'close') closeNicknameModal();
    else if (action === 'save') saveNickname();
  });

  requestAnimationFrame(() => {
    document.getElementById('nicknameInput').value = getUserNickname() || '';
    modal.classList.add('open');
    document.getElementById('nicknameInput')?.focus();
    if (window.ModalManager) {
      window.ModalManager.register('nicknameModal', closeNicknameModal, 'bottomSheet');
      window.ModalManager.push('nicknameModal');
    }
  });
}

export function closeNicknameModal() {
  const modal = document.getElementById('nicknameModal');
  if (modal) {
    modal.classList.remove('open');
    setTimeout(() => {
      modal.remove();
      if (window.ModalManager) {
        window.ModalManager.unregister('nicknameModal');
      }
    }, 300);
  }
  if (window.ModalManager) {
    window.ModalManager.remove('nicknameModal', true);
  }
}

export async function saveNickname() {
  const newNickname = document.getElementById('nicknameInput').value.trim();
  const currentUser = getCurrentUser();

  if (!newNickname || newNickname.length < 2) {
    showToast('닉네임을 2자 이상 입력하세요', true);
    return;
  }

  if (newNickname.length > 12) {
    showToast('닉네임은 12자 이하로 입력하세요', true);
    return;
  }

  showGlobalLoading();

  try {
    const response = await authFetch(USER_ENDPOINTS.updateNickname, {
      body: { nickname: newNickname }
    });

    if (!response.ok) throw new Error('서버 오류');

    setUserNickname(newNickname);
    updateNicknameDisplay();
    closeNicknameModal();
    showToast('닉네임이 변경되었습니다');
  } catch (e) {
    showToast('닉네임 변경 실패: ' + e.message, true);
  } finally {
    hideGlobalLoading();
  }
}
